import { useAtom } from '@reatom/npm-react'
import { memo, useMemo } from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { Tooltip } from '@blade/ui-kit'
import { FlashTextContentDistinct } from '@root/features/flash/flash-text-content-distinct'
import { NumericFormat } from '@root/shared/core/format'
import { CrosshairIcon } from '@root/shared/ui-kit'

import { PairExploreItemAuditItem } from '../pair-explore-item-audit-item'

import { PairExploreItemAuditSnipersTooltip } from './pair-explore-item-audit-snipers-tooltip'

import type { PairAudit, PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  pair: PairModel
  size?: number
} & ClassNameProps

export const PairExploreItemAuditSnipers = memo(
  ({ className, size = 12, pair }: Props) => {
    const [aggregatedHolding] = useAtom(
      pair.ui.snipersInsidersBundlersHoldingAtom,
    )
    const aggregatedHoldingRounded = aggregatedHolding.gte(10)
      ? aggregatedHolding.integerValue()
      : aggregatedHolding.decimalPlaces(1)

    const holdingFormatted = useMemo(
      () => NumericFormat.formatPreset(aggregatedHoldingRounded, 'percents'),
      [aggregatedHoldingRounded],
    )

    const intent = useMemo(() => {
      return match(aggregatedHolding)
        .returnType<PairAudit.AuditStatus>()
        .when(
          (it) => it.gte(40),
          () => 'Danger',
        )
        .when(
          (it) => it.gte(20),
          () => 'Warning',
        )
        .otherwise(() => 'Safe')
    }, [aggregatedHolding])

    return (
      <Root className={className}>
        <Tooltip content={<PairExploreItemAuditSnipersTooltip pair={pair} />}>
          <PairExploreItemAuditItem pair={pair} intent={intent}>
            <CrosshairIcon height={size} />{' '}
            {aggregatedHoldingRounded.isZero() ? undefined : (
              <FlashTextContentDistinct>
                {holdingFormatted}%
              </FlashTextContentDistinct>
            )}
          </PairExploreItemAuditItem>
        </Tooltip>
      </Root>
    )
  },
)

const Root = styled.div`
  gap: 4px;

  display: grid;
  grid-auto-flow: column;
  justify-items: start;
  align-items: center;
  justify-content: start;
`
